import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './public-report.component.scss';
import config from '../../config';
import apiConfig from '../../api.config';
import { ReportModel } from '../../model/report.modelt';

const PublicReport: React.FC = () => {
    const { reportId } = useParams();
    const [reportData, setReportData] = useState<ReportModel | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchReportData = async () => {
            try {
                if (reportId) {
                    const response = await fetch(`${config.api.url}/report/${reportId}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    });

                    if (!response.ok) {
                        throw new Error('Failed to fetch report data');
                    }

                    const result = await response.json();
                    setReportData(result.data as ReportModel);
                }
            } catch (error) {
                console.error('Error fetching report data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchReportData();
    }, [reportId]);

    const handlePrint = () => {
        window.print();
    };

    const formatDuration = (duration: string) => {
        if (!duration) return 'N/A';
        
        // Handle time format "HH:MM:SS" or "MM:SS"
        if (duration.includes(':')) {
            const parts = duration.split(':');
            if (parts.length === 3) {
                // HH:MM:SS format
                const hours = parseInt(parts[0]);
                const minutes = parseInt(parts[1]);
                const seconds = parseInt(parts[2]);
                const totalSeconds = hours * 3600 + minutes * 60 + seconds;
                const displayMinutes = Math.floor(totalSeconds / 60);
                const displaySeconds = totalSeconds % 60;
                return `${displayMinutes}m ${displaySeconds}s`;
            } else if (parts.length === 2) {
                // MM:SS format
                const minutes = parseInt(parts[0]);
                const seconds = parseInt(parts[1]);
                return `${minutes}m ${seconds}s`;
            }
        }
        
        // Handle numeric seconds
        const totalSeconds = parseFloat(duration);
        if (isNaN(totalSeconds) || totalSeconds < 0) return 'N/A';
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = Math.floor(totalSeconds % 60);
        return `${minutes}m ${seconds}s`;
    };

    const calculateDurationDifference = (endTime: string, startTime: string) => {
        if (!endTime || !startTime) return 'N/A';
        
        const parseTimeToSeconds = (timeStr: string) => {
            if (timeStr.includes(':')) {
                const parts = timeStr.split(':');
                if (parts.length === 3) {
                    const hours = parseInt(parts[0]);
                    const minutes = parseInt(parts[1]);
                    const seconds = parseInt(parts[2]);
                    return hours * 3600 + minutes * 60 + seconds;
                } else if (parts.length === 2) {
                    const minutes = parseInt(parts[0]);
                    const seconds = parseInt(parts[1]);
                    return minutes * 60 + seconds;
                }
            }
            return parseFloat(timeStr) || 0;
        };
        
        const endSeconds = parseTimeToSeconds(endTime);
        const startSeconds = parseTimeToSeconds(startTime);
        const difference = endSeconds - startSeconds;
        
        if (difference <= 0) return 'N/A';
        const minutes = Math.floor(difference / 60);
        const seconds = difference % 60;
        return `${minutes}m ${seconds}s`;
    };

    const getScoreColor = (score: number) => {
        if (score >= 80) return '#10b981'; // Green
        if (score >= 60) return '#f59e0b'; // Yellow
        return '#ef4444'; // Red
    };

    if (isLoading) {
        return (
            <div className="public-report-loading">
                <div className="loading-spinner"></div>
                <p>Loading candidate report...</p>
            </div>
        );
    }

    if (!reportData) {
        return (
            <div className="public-report-error">
                <h2>Report Not Found</h2>
                <p>The requested candidate report could not be loaded.</p>
            </div>
        );
    }

    const showVideoPerQuestion = Boolean(reportData.job_position?.hardware_require?.record_video);
    const getMediaSrc = (url?: string) => (url ? String(url).split("&token")[0] : "");

    return (
        <div className="public-report-container">
            {/* Print Button */}
            <div className="print-actions">
                <button onClick={handlePrint} className="print-button">
                    🖨️ Print Candidate Report
                </button>
            </div>

            {/* Resume-Style Document */}
            <div className="resume-document">
                {/* Header Section */}
                <header className="resume-header">
                    <div className="candidate-info">
                        <h1 className="candidate-name">
                            {reportData.candidate_detail?.firstname} {reportData.candidate_detail?.lastname}
                        </h1>
                        <p className="candidate-title">{reportData.candidate_detail?.jobTitle}</p>
                        <div className="contact-info">
                            <span>📧 {reportData.candidate_detail?.email}</span>
                            <span>📱 {reportData.candidate_detail?.phone}</span>
                            <span>💼 {reportData.candidate_detail?.jobExp} years experience</span>
                        </div>
                    </div>
                    <div className="position-info">
                        <h2 className="position-title">{reportData.job_position?.job_title}</h2>
                        <p className="company-name">{reportData.job_position?.name}</p>
                        <div className="position-details">
                            <span>{reportData.job_position?.job_level}</span>
                            <span>•</span>
                            <span>{reportData.job_position?.job_department}</span>
                            <span>•</span>
                            <span>{reportData.job_position?.job_industry}</span>
                        </div>
                    </div>
                </header>

                {/* Executive Summary */}
                <section className="resume-section executive-summary">
                    <h3 className="section-title">Executive Summary</h3>
                    <div className="summary-content">
                        {reportData.report?.all_summary_transcript || 
                         'AI-powered interview analysis completed. Candidate demonstrated relevant skills and experience for the position.'}
                    </div>
                </section>
 {/* Resume Summary */}
 {reportData.candidate_detail?.resume?.summary && (
                    <section className="resume-section resume-summary">
                        <h3 className="section-title">Resume Summary</h3>
                        <div className="summary-content">
                            {reportData.candidate_detail.resume.summary}
                        </div>
                    </section>
                )}
                {/* Assessment Scores */}
                <section className="resume-section assessment-scores">
                    <h3 className="section-title">Assessment Results</h3>
                    <div className="scores-grid">
                        <div className="score-card">
                            <div className="score-header">
                                <span className="score-label">Overall Fit</span>
                                <span className="score-value" style={{color: getScoreColor(reportData.score?.job_fit_score || 0)}}>
                                    {reportData.score?.job_fit_score || 'N/A'}
                                </span>
                            </div>
                            <div className="score-bar">
                                <div className="score-fill" style={{
                                    width: `${reportData.score?.job_fit_score || 0}%`,
                                    backgroundColor: getScoreColor(reportData.score?.job_fit_score || 0)
                                }}></div>
                            </div>
                        </div>

                        <div className="score-card">
                            <div className="score-header">
                                <span className="score-label">Skill Match</span>
                                <span className="score-value" style={{color: getScoreColor(reportData.score?.skill_fit_score?.skill_fit_percentage || 0)}}>
                                    {reportData.score?.skill_fit_score?.skill_fit_percentage || 'N/A'}%
                                </span>
                            </div>
                            <div className="score-bar">
                                <div className="score-fill" style={{
                                    width: `${reportData.score?.skill_fit_score?.skill_fit_percentage || 0}%`,
                                    backgroundColor: getScoreColor(reportData.score?.skill_fit_score?.skill_fit_percentage || 0)
                                }}></div>
                            </div>
                        </div>

                        <div className="score-card">
                            <div className="score-header">
                                <span className="score-label">Experience Fit</span>
                                <span className="score-value" style={{color: getScoreColor(reportData.score?.experience_fit_score?.final_score || 0)}}>
                                    {reportData.score?.experience_fit_score?.final_score || 'N/A'}
                                </span>
                            </div>
                            <div className="score-bar">
                                <div className="score-fill" style={{
                                    width: `${reportData.score?.experience_fit_score?.final_score || 0}%`,
                                    backgroundColor: getScoreColor(reportData.score?.experience_fit_score?.final_score || 0)
                                }}></div>
                            </div>
                        </div>

                        <div className="score-card">
                            <div className="score-header">
                                <span className="score-label">Knowledge Test</span>
                                <span className="score-value">
                                    {reportData.score?.multiple_choice_score?.score || 'N/A'}/{reportData.score?.multiple_choice_score?.total_questions || 'N/A'}
                                </span>
                            </div>
                            <div className="score-bar">
                                <div className="score-fill" style={{
                                    width: `${reportData.score?.multiple_choice_score?.score && reportData.score?.multiple_choice_score?.total_questions ? 
                                        (reportData.score.multiple_choice_score.score / reportData.score.multiple_choice_score.total_questions) * 100 : 0}%`,
                                    backgroundColor: '#3182ce'
                                }}></div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Skills Assessment */}
                {reportData.report?.summary_skills && reportData.report.summary_skills.length > 0 && (
                    <section className="resume-section skills-assessment">
                        <h3 className="section-title">Skills Assessment</h3>
                        <div className="skills-grid">
                            {reportData.report.summary_skills.map((item: any, idx: number) => (
                                <div key={idx} className="skill-item">
                                    <div className="skill-header">
                                        <span className="skill-name">{item.skill}</span>
                                        <span className="skill-topic">{item.topic}</span>
                                    </div>
                                    <p className="skill-summary">{item.summary}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Interview Transcript */}
                {reportData.transcript?.logs && reportData.transcript.logs.length > 0 && (
                    <section className="resume-section interview-transcript">
                        <h3 className="section-title">
                            Interview Responses
                            {showVideoPerQuestion && (
                                <span className="section-badge">Video per question</span>
                            )}
                        </h3>
                        <div className="transcript-container">
                            {reportData.transcript.logs.map((log: any, idx: number) => {
                                let answer = '';
                                let questionTypeLabel = '';
                                const isVoice = log.question_type === 'voice';
                                
                                if (isVoice) {
                                    questionTypeLabel = 'Voice Response';
                                    const matchedAnswer = (reportData.transcript.answers || []).find((ans: any) => ans.question_id === log.question_id);
                                    answer = matchedAnswer ? matchedAnswer.text : '';
                                } else {
                                    questionTypeLabel = 'Multiple Choice';
                                    answer = log.response && typeof log.response === 'object' ? log.response.value : String(log.response || '');
                                }

                                // Calculate detailed duration
                                const responseDurationFormatted = calculateDurationDifference(log.duration_end, log.question_read_end);

                                // Find matching speech analysis
                                const speechAnalysis = reportData.report?.speech_analysis?.find((analysis: any) => 
                                    analysis.question_id === log.question_id
                                );
                                const videoSrc = getMediaSrc(log.video_url);
                                const audioSrc = getMediaSrc(log.audio_url);
                                const showVideo = Boolean(videoSrc && (showVideoPerQuestion || log.video_url));
                                
                                return (
                                    <div key={idx} className="transcript-item">
                                        <div className="question-section">
                                            <div className="question-number">Q{idx + 1}</div>
                                            <div className="question-content">
                                                <p className="question-text">
                                                    {(log.question && (log.question.th || log.question.en)) ? String(log.question.th || log.question.en) : ''}
                                                </p>
                                                <span className="question-type">{questionTypeLabel}</span>
                                            </div>
                                        </div>
                                        {(showVideo || log.snapshot || audioSrc) && (
                                            <div className="answer-media-section no-print">
                                                {log.snapshot && (
                                                    <img
                                                        src={log.snapshot}
                                                        alt={`Snapshot Q${idx + 1}`}
                                                        className="answer-snapshot"
                                                    />
                                                )}
                                                {audioSrc && (
                                                    <div className="answer-media-block">
                                                        <span className="answer-media-label">Audio Response</span>
                                                        <audio controls preload="metadata" className="answer-audio-player" src={audioSrc}>
                                                            Your browser does not support the audio element.
                                                        </audio>
                                                    </div>
                                                )}
                                                {showVideo && (
                                                    <div className="answer-media-block">
                                                        <span className="answer-media-label">Video Response</span>
                                                        <video
                                                            controls
                                                            preload="metadata"
                                                            className="answer-video-player"
                                                            src={videoSrc}
                                                        >
                                                            Your browser does not support the video element.
                                                        </video>
                                                        {log.video_duration_sec != null && (
                                                            <span className="answer-video-duration">
                                                                Duration: {Math.round(Number(log.video_duration_sec))}s
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="answer-section">
                                            <div className="answer-content">
                                                <p className="answer-text">{answer}</p>
                                                <div className="answer-details">
                                                    <span className="answer-duration">
                                                        Response Time: {responseDurationFormatted}
                                                    </span>
                                                   
                                                </div>
                                            </div>
                                        </div>
                                        {speechAnalysis && (
                                            <div className="speech-analysis-section">
                                                <div className="analysis-header">
                                                    <span className="analysis-icon">🎤</span>
                                                    <span className="analysis-label">Speech Analysis</span>
                                                </div>
                                                <div className="analysis-content">
                                                    {speechAnalysis.speech_analysis}
                                                </div>
                                            </div>
                                        )}
                                        {log.emotion && (
                                            <div className="emotion-analysis-section">
                                                <div className="analysis-header">
                                                    <span className="analysis-icon">🎭</span>
                                                    <span className="analysis-label">Emotion Analysis</span>
                                                </div>
                                                <div className="analysis-content">{log.emotion}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

               

                {/* Footer */}
                <footer className="resume-footer">
                    <div className="footer-content">
                        <p className="generated-by">Generated by Interview Question JobHack System</p>
                        <p className="report-id">Report ID: {reportId}</p>
                        <p className="generated-date">
                            Generated on {new Date().toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric'
                            })}
                        </p>
                        <p className="confidentiality">This report is confidential and intended for authorized personnel only.</p>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default PublicReport; 